import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { repeat } from '@nullvoxpopuli/ember-composable-helpers';
import type AbilitiesService from 'ember-can/services/abilities';
import MarkdownToHtml from 'ember-cli-showdown/components/markdown-to-html';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { pluralize } from 'ember-inflector';
import add from 'ember-math-helpers/helpers/add';
import sub from 'ember-math-helpers/helpers/sub';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, notEq, or } from 'ember-truth-helpers';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import Card from 'garaje/components/ui/card';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import UiButton from 'garaje/components/ui-button';
import type ScreenerResult from 'garaje/models/screener-result';
import type { ScreenerResultDetailsField, ScreenerResultDetailsMatch } from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import { BlocklistFilterSource } from 'garaje/utils/enums';
import { TrackedWeakMap } from 'tracked-built-ins';

interface UnifiedScreenersModalSignature {
  Args: {
    model: GuestWithVisitApproval;
    focusedResult?: ScreenerResult;
    onClose: () => void;
    /** Approval context used to authorize visible results; inferred from the focused result when omitted. */
    context?: 'location' | 'property';
  };
  Blocks: {
    actions: [ScreenerResult, onApprove: () => void];
  };
}

export default class UnifiedScreenersModal extends Component<UnifiedScreenersModalSignature> {
  @service declare abilities: AbilitiesService;

  fieldVisibilityPerMatch = new TrackedWeakMap<ScreenerResultDetailsMatch, boolean>();

  @tracked selectedIndex = 0;

  get selectedResult(): ScreenerResult {
    return this.visibleResults[this.selectedIndex]!;
  }

  get isLast(): boolean {
    return this.selectedIndex === this.visibleResults.length - 1;
  }

  // Drives the prev/next bar; also whether the fixed bottom bars stack.
  get hasMultipleScreeners(): boolean {
    return this.visibleResults.length > 1;
  }

  // Phone: the action bar stacks above the prev/next bar when there are multiple
  // screeners; with one screener it is the bottom-most bar and absorbs the safe area
  // itself. Desktop (sm:) restores it into the left panel flow.
  get actionBarClass(): string {
    const base =
      'fixed inset-x-0 z-30 border-t border-carbon-10 bg-white px-4 sm:static sm:bottom-auto sm:z-auto sm:border-0 sm:bg-transparent sm:p-0';
    const placement = this.hasMultipleScreeners
      ? 'bottom-[calc(3.75rem_+_env(safe-area-inset-bottom))] py-3'
      : 'bottom-0 pt-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]';
    return `${base} ${placement}`;
  }

  // Phone: reserve scroll space for only the fixed bottom bars that render
  // (action bar ~5.25rem, prev/next bar 3.75rem).
  contentBottomPadding = (hasActions: boolean): string => {
    if (hasActions) {
      return this.hasMultipleScreeners
        ? 'pb-[calc(9rem_+_env(safe-area-inset-bottom))]'
        : 'pb-[calc(5.25rem_+_env(safe-area-inset-bottom))]';
    }
    return this.hasMultipleScreeners
      ? 'pb-[calc(3.75rem_+_env(safe-area-inset-bottom))]'
      : 'pb-[env(safe-area-inset-bottom)]';
  };

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    const result = this.args.focusedResult;
    let context = this.args.context ?? 'location';

    if (!this.args.context && result?.isBlocklist()) {
      const matchedFilters = result.report.matchedFilters ?? [];
      const hasPropertyFilter = matchedFilters.some((filter) => filter.filterSource === BlocklistFilterSource.PROPERTY);
      const hasLocationFilter = matchedFilters.some((filter) => filter.filterSource === BlocklistFilterSource.LOCATION);

      if (hasPropertyFilter && !hasLocationFilter) {
        context = 'property';
      }
    }

    return {
      context,
      parent: this.args.model,
      visitApproval: this.args.model.visitApproval,
    };
  }

  get isVisualCompliance(): boolean {
    return this.selectedResult?.isVisualCompliance() ?? false;
  }

  get isBlocklist(): boolean {
    return this.selectedResult?.isBlocklist() ?? false;
  }

  get visibleResults(): ScreenerResult[] {
    // look at all screeners not just in review to avoid this getter from recomputing
    // and messing with the selected index while navigating
    const visibleResults = this.args.model.visitApproval.screenerResults.filter((result) => {
      const source = sourceToAbilityMap[result.screenerSource] || result.screenerSource;
      const canView = this.abilities.can(`view ${source}`, this.#permissionsModel);
      const canReview = this.abilities.can(`review ${source}`, this.#permissionsModel);

      return result.details && (canView || canReview);
    });

    const { focusedResult } = this.args;

    // bring result into focus if the modal was opened from the scope of a specific result
    const index = visibleResults.findIndex((result) => result.screenerSource === focusedResult?.screenerSource);
    if (index !== -1) {
      const [focused] = visibleResults.splice(index, 1);
      visibleResults.unshift(focused!);
    }

    return visibleResults;
  }

  get visibleReviewableResults(): ScreenerResult[] {
    return this.visibleResults.filter((result) => result.isReview);
  }

  @action
  getFieldsForMatch(match: ScreenerResultDetailsMatch): ScreenerResultDetailsField[] {
    const max = match['visible-fields-count'];

    // eslint-disable-next-line ember/use-ember-get-and-set
    const showFields = this.fieldVisibilityPerMatch.get(match) ?? false;
    const lessThanMax = max ? match.fields.length < max : true;

    if (showFields || lessThanMax) return match.fields;
    return match.fields.slice(0, max!) ?? [];
  }

  @action
  toggleVisibility(match: ScreenerResultDetailsMatch): void {
    // eslint-disable-next-line ember/use-ember-get-and-set
    this.fieldVisibilityPerMatch.set(match, !this.fieldVisibilityPerMatch.get(match));
  }

  @action
  getVisibilityPerMatch(match: ScreenerResultDetailsMatch): boolean | undefined {
    // eslint-disable-next-line ember/use-ember-get-and-set
    return this.fieldVisibilityPerMatch.get(match);
  }

  @action
  prev(): void {
    this.selectedIndex -= 1;
  }

  @action
  next(): void {
    this.selectedIndex += 1;
  }

  @action
  didApprove(): void {
    // if there are no more reviewable results left, close the modal
    if (this.visibleReviewableResults.length === 0) {
      this.args.onClose();
      return;
    }

    if (this.isLast) {
      // go to the first reviewable result if we're at the end of the carousel
      this.selectedIndex = this.visibleResults.indexOf(this.visibleReviewableResults[0]!);
    } else {
      // go to the next result
      this.next();
    }
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @translucentOverlay={{true}}
      @responsive={{true}}
      @containerClass="ember-modal-dialog--unified-screeners"
      @onClose={{@onClose}}
    >
      <FocusTrapper>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="unified-screeners-modal-title"
          class="flex flex-col {{this.contentBottomPadding (has-block 'actions')}} sm:flex-row sm:pb-0"
          data-test-unified-screeners-modal={{this.selectedResult.id}}
          data-test-unified-screeners-modal-screener-name={{this.selectedResult.screenerSource}}
        >
          <section class="contents sm:block sm:w-142 sm:bg-carbon-5 sm:pt-10 sm:pb-8 sm:px-8">
            {{! Phone: sticky top bar (like Visitors::StickyContextHeader); sm: restores the in-panel heading. }}
            <div
              class="sticky top-0 z-30 flex items-center gap-3 border-b border-carbon-10 bg-white pl-4 pr-14 py-3 sm:static sm:z-auto sm:items-stretch sm:gap-0 sm:border-b-0 sm:bg-transparent sm:pl-0 sm:pr-0 sm:py-0"
            >
              {{svgJar "logo-small-red" height="16" width="16" class="h-7 w-7 shrink-0 sm:h-4 sm:w-4"}}
              <div class="flex flex-col min-w-0 sm:ml-2">
                <h4 class="m-0 text-carbon-90 sm:mb-2 sm:text-carbon-70">
                  {{if this.isBlocklist "Matched data from this visitor" "Matched data references from Envoy"}}
                </h4>
                {{#if (has-block "actions")}}
                  <p class="m-0 text-sm leading-tight text-carbon-50 sm:mb-4 sm:leading-6-alt sm:text-carbon-60">
                    Please review before taking any action.
                  </p>
                {{/if}}
              </div>
            </div>
            {{! Phone: fixed bottom bar, above the prev/next bar; sm: restores it inside the panel. }}
            {{#if (has-block "actions")}}
              <div class={{this.actionBarClass}}>
                {{yield this.selectedResult this.didApprove to="actions"}}
              </div>
            {{/if}}

            {{#if this.selectedResult.details.input.fields.length}}
              <h4 class="mt-4 !text-sm sm:mt-6 mx-4 sm:mx-0 sm:!text-base">
                Submitted reference
              </h4>
              <Card class="mx-4 mt-2 sm:mx-0 border-carbon-10 sm:border-carbon-20 mb-6 sm:mb-0" data-test-input-details>
                {{#each this.selectedResult.details.input.fields as |field|}}
                  <div class="flex flex-col" data-test-input-details-field>
                    <span class="text-sm !text-carbon-30 font-normal">{{field.name}}:</span>
                    <p
                      class="text-sm sm:text-base font-bold {{if field.matched 'text-papaya-50'}}"
                      data-test-input-value={{field.name}}
                      data-test-field-matched={{if field.matched "true" "false"}}
                      data-private
                    >{{if field.value field.value "--"}}{{#if field.matched}}<span class="sr-only">, matched</span>{{/if}}</p>
                  </div>
                {{/each}}
              </Card>
            {{/if}}
          </section>

          <section
            class="w-full flex flex-col border-t sm:h-[min(715px,80vh)] sm:border-t-0"
            data-test-unified-screeners-modal-content
          >
            <header class="flex justify-between items-end mx-4 sm:mx-8 my-6 mb-2">
              <h4 id="unified-screeners-modal-title" class="!text-sm sm:!text-base">
                {{if this.isBlocklist "Flagged results from Block list" "Flagged Results"}}
              </h4>
              {{! Phone: pin the close into the sticky top bar (top-right, above it); sm: back into this header. }}
              <button
                data-test-trigger-close
                class="btn btn--small btn--outline btn--cancel fixed top-3 right-4 z-40 sm:static sm:top-auto sm:right-auto sm:z-auto"
                {{on "click" @onClose}}
                type="button"
              >
                <span aria-hidden="true">&times;</span>
                <span class="sr-only">close</span>
              </button>
            </header>

            {{#if this.selectedResult.details.matches}}
              <div class="sm:overflow-y-auto">
                <Card class="mx-4 sm:mx-8 mt-2 mb-8 sm:mt-8" data-test-match-details>
                  <div class="flex items-center py-2">
                    {{#if (get this.selectedResult.details "logo-url")}}
                      <img
                        class="w-5 h-5 mr-2"
                        src={{get this.selectedResult.details "logo-url"}}
                        alt=""
                        role="presentation"
                      />
                    {{/if}}
                    <h3 class="!text-sm sm:!text-lg">
                      {{this.selectedResult.details.title}}
                      <span class="text-papaya-50 font-normal">•
                        {{pluralize this.selectedResult.details.matches.length "match"}}
                      </span>
                    </h3>

                  </div>
                  <MarkdownToHtml
                    data-test-subtitle
                    class="text-sm"
                    @markdown={{this.selectedResult.details.subtitle}}
                  />

                  {{#if this.isBlocklist}}
                    <div
                      class="flex items-start gap-2 bg-gem-5 rounded-md p-3 my-3 text-sm text-carbon-70"
                      data-test-blocklist-match-info
                    >
                      {{svgJar "banner-info" height="16" width="16" class="flex-none mt-0.5"}}
                      <span>These details are used to match visitors against this block list entry.</span>
                    </div>
                  {{/if}}

                  {{#each this.selectedResult.details.matches as |match index|}}
                    <table data-test-match={{index}} class="my-3">
                      <tr>
                        <th
                          class="rounded-tl-md w-28 h-9 text-xs text-left text-carbon-50 align-middle font-semibold bg-carbon-5 border border-carbon-10"
                        >
                          {{match.headers.name}}
                        </th>
                        <th
                          class="rounded-tr-md w-28 h-9 text-xs text-left text-carbon-50 align-middle font-semibold bg-carbon-5 border border-carbon-10"
                        >
                          <div class="flex justify-between items-center">
                            {{match.headers.value}}
                            {{#let (get match "alert-level") as |alertLevel|}}
                              {{#if alertLevel}}
                                <div
                                  class="flex items-center px-2 py-0.5 rounded-full
                                    {{if (eq alertLevel 1) 'bg-papaya-10' 'bg-red-10'}}"
                                >
                                  {{#each (repeat alertLevel) as |_ idx|}}
                                    <div
                                      class="rounded-full mr-1 w-[10px] h-[10px] {{if idx 'bg-red-50' 'bg-papaya-30'}}"
                                    >
                                    </div>
                                  {{/each}}
                                  <div
                                    class="ml-1 mt-0.5 tracking-wide
                                      {{if (eq alertLevel 1) 'text-papaya-50' 'text-red-60'}}"
                                    data-test-match-attr-alert-level
                                  >
                                    LEVEL
                                    {{alertLevel}}
                                  </div>
                                </div>
                              {{/if}}
                            {{/let}}
                          </div>
                        </th>
                      </tr>
                      {{#let (this.getFieldsForMatch match) as |fields|}}
                        {{#each fields as |field index|}}
                          <tr data-test-match-attr={{field.name}}>
                            <th
                              class="w-28 text-left align-top font-semibold py-2 border-x border-b border-carbon-10 text-sm sm:text-base
                                {{if (eq index (sub match.fields.length 1)) 'rounded-bl-md'}}"
                              data-test-match-attr-label={{field.name}}
                            >
                              {{field.name}}
                            </th>
                            <td
                              class="text-left align-top py-2 border-r border-b border-carbon-10 text-sm sm:text-base
                                {{if field.matched 'text-papaya-50 font-bold' 'text-carbon-50'}}
                                {{if (eq index (sub match.fields.length 1)) 'rounded-br-md'}}"
                              data-test-field-value={{field.name}}
                              data-test-field-matched={{if field.matched "true" "false"}}
                              data-private
                            >
                              {{#if (eq field.type "image")}}
                                <img src={{field.value}} alt="" role="presentation" class="w-28 h-28 object-cover" />
                              {{else}}
                                {{field.value}}
                              {{/if}}
                              {{#if field.matched}}<span class="sr-only">, matched</span>{{/if}}
                            </td>
                          </tr>
                        {{/each}}
                        {{#let (notEq fields.length match.fields.length) as |notRenderingAllFields|}}
                          {{#if (or notRenderingAllFields (eq (this.getVisibilityPerMatch match) true))}}
                            <tr>
                              <td
                                class="w-28 text-left align-top font-semibold py-2 border-x border-b border-carbon-10 rounded-bl-md"
                              >
                              </td>
                              <td
                                class="w-28 align-top font-semibold py-2 border-x border-b border-carbon-10 rounded-br-md text-center"
                              >
                                <UiButton
                                  {{on "click" (fn this.toggleVisibility match)}}
                                  class="text-sm text-gem-60"
                                  type="button"
                                  data-test-toggle-fields-visibility
                                >
                                  {{svgJar
                                    "chevron-down-gem-50"
                                    height="16"
                                    width="16"
                                    class=(concat "align-middle " (unless notRenderingAllFields "rotate-180" ""))
                                  }}
                                  {{if notRenderingAllFields "Show more" "Show less"}}
                                </UiButton>
                              </td>
                            </tr>
                          {{/if}}
                        {{/let}}
                      {{/let}}
                    </table>
                  {{/each}}
                </Card>
              </div>
            {{else}}
              <Card class="mx-4 sm:mx-8 my-8" data-test-match-attr="no-data">
                <p class="text-sm !mb-0">
                  Currently, we don't have any further details on this match.
                  {{#if this.isVisualCompliance}}For more information, please visit the
                    <a href="https://www.visualcompliance.com" target="_blank" rel="noopener">
                      Visual Compliance dashboard →
                    </a>
                  {{/if}}
                </p>
              </Card>
            {{/if}}
          </section>

        </div>
        {{! Phone: fixed bottom bar beneath the actions; sm: restores the in-flow bottom-right nav. }}
        {{#if this.hasMultipleScreeners}}
          <div
            class="fixed inset-x-0 bottom-0 z-30 flex items-center justify-end gap-x-4 border-t border-carbon-10 bg-white px-4 pt-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] sm:static sm:z-auto sm:border-t-carbon-5 sm:bg-transparent sm:p-4"
          >
            <span class="text-sm text-carbon-50 mr-4" data-test-screener-progress>
              {{add this.selectedIndex 1}}
              of
              {{this.visibleResults.length}}
              {{pluralize this.visibleResults.length "screener" withoutCount=true}}
            </span>
            <UiButton
              class="btn btn--small btn--outline [&_path]:fill-carbon-50 flex items-center sm:w-30"
              disabled={{eq this.selectedIndex 0}}
              {{on "click" this.prev}}
              data-test-prev-button
            >
              {{svgJar "chevron-left" height="12" width="12" class="mr-1"}}
              Prev
            </UiButton>

            <UiButton
              class="btn btn--small btn--outline [&_path]:fill-carbon-50 flex items-center sm:w-30"
              disabled={{this.isLast}}
              {{on "click" this.next}}
              data-test-next-button
            >
              Next
              {{svgJar "chevron-left" height="12" width="12" class="rotate-180 ml-1"}}
            </UiButton>
          </div>
        {{/if}}
      </FocusTrapper>
    </ModalDialog>
  </template>
}
